<template>
  <article
    class="bg-white p-4 md:py-6 md:px-8 rounded-3xl shadow-combined-elevation-3 flex flex-col md:grid md:grid-cols-[96px,1fr] lg:grid-cols-[128px,1fr] gap-4 md:gap-8"
    :data-testid="dataTestid"
  >
    <div class="relative wh-24 lg:wh-32 aspect-square rounded-full">
      <img
        :src="props.author?.image"
        :alt="props.author?.name"
        class="rounded-full
        object-cover"
        width="128"
        height="128"
      />

      <div class="absolute bottom-0 right-0 bg-base-lime-lime wh-[2.375rem] rounded-full border-2 border-white flex items-center justify-center">
        <V2IconsCheck
          width="18"
          height="18"
        />
      </div>
    </div>

    <div class="flex flex-col items-start gap-4">
      <div class="flex flex-col gap-1">
        <strong
          v-if="!props.hideEditorialBadge"
          class="text-sm text-semantic-primary-default"
        >
          Compare Club Editorial
        </strong>

        <component
          :is="props.headerTag"
          class="text-semantic-sizing-headline-sm font-semibold leading-[1.05] text-semantic-secondary-variant-dark"
        >
          {{ props.author?.name }}
        </component>

        <p
          v-if="props.author?.jobTitle"
          class="text-semantic-sizing-label-lg font-bold text-semantic-surface-on-surface-variant"
        >
          {{ props.author?.jobTitle }}

          <span v-if="props.author?.officeLocation">
            — {{ props.author?.officeLocation }}
          </span>
        </p>
      </div>

      <div
        v-if="!_isEmpty(knownFor) && props.showKnownFor"
        class="flex flex-wrap gap-2"
      >
        <V2AtomsTag
          v-for="(item, itemIndex) in knownFor"
          :key="`known-for-${itemIndex}`"
          color="whiteb"
          class="py-1 px-3"
        >
          {{ item }}
        </V2AtomsTag>
      </div>

      <p
        class="text-semantic-surface-on-surface text-semantic-sizing-body-sm"
      >
        {{ authorProfile }}
      </p>

      <div
        v-if="!_isEmpty(statistics)"
        class="flex flex-col lg:flex-row flex-wrap gap-4 lg:gap-0 border-t-1 border-semantic-outline-variant pt-4 w-full"
        :class="{
          'border-b-1 pb-4': !_isEmpty(props.author?.socialMedia) || props.showLink
        }"
      >
        <div
          v-for="(item, itemIndex) in statistics"
          :key="`author-stats-${itemIndex}`"
          class="flex text-semantic-sizing-label-lg text-semantic-surface-on-surface-variant font-bold"
        >
          <div class="flex flex-col">
            <span class="text-semantic-sizing-headline-sm font-semibold text-semantic-primary-default">
              {{ item?.value }}
            </span>

            {{ item?.label }}
          </div>

          <div
            v-if="itemIndex !== statistics.length - 1"
            class="w-[1px] h-full hidden lg:block bg-semantic-outline-variant md:mx-5 xl:mx-10"
          >
          </div>
        </div>
      </div>

      <V2AtomsButton
        v-if="props.showLink"
        size="small"
        :to="trailingSlash(`/our-experts${props.author?.url}`)"
        has-icon-right
        @click="authorCardTracking"
      >
        Read full profile
      </V2AtomsButton>

      <V2MoleculesAuthorSocialMediaList :list="props.author?.socialMedia" />
    </div>
  </article>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

defineOptions({
  name: 'V2MoleculesAuthorProfileCard'
})

const { $tracking } = useNuxtApp()

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'author-profile-card'
  },

  headerTag: {
    type: String,
    default: 'h1',
    validator: value => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div'].includes(value)
  },

  author: {
    type: Object,
    default: () => ({})
  },

  hideEditorialBadge: {
    type: Boolean,
    default: false
  },

  showLink: {
    type: Boolean,
    default: false
  },

  showKnownFor: {
    type: Boolean,
    default: false
  },

  showFullProfile: {
    type: Boolean,
    default: false
  },

  knownForLimit: {
    type: Number,
    default: null
  },

  minPostCount: {
    type: Number,
    default: 1
  }
})

const authorProfile = computed(() => {
  if (props.showFullProfile) {
    return props.author?.profile
  }

  const profile = !_isEmpty(props.author?.profileShort)
    ? props.author?.profileShort
    : props.author?.profile

  return profile?.length > 360 ? props.author?.profileExcerpt : profile
})

const knownFor = computed(() => {
  const list = props.author?.knownFor || []

  return props.knownForLimit
    ? list.slice(0, props.knownForLimit)
    : list
})

const statistics = computed(() => {
  const totalPosts = props.author?.totalPosts ?? props.author?.statsGuides

  return [
    {
      label: 'Guides written or edited',
      value: totalPosts >= props.minPostCount ? `${totalPosts}` : 0
    },
    ...(props.author?.statistics ?? [])
  ].filter(item => item?.value)
})

function authorCardTracking () {
  $tracking.customEvent('author_card_click', {
    person_slug: trailingSlash(`/our-experts${props.author?.url}`),
    category: props.author?.vertical
  })
}
</script>
